import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppButton, Box, DropdownInput, OptionKey, PlusThinIcon, Text, useResponsive } from '@common-fe/common-fe';
import { toString } from 'lodash';

import HorizontalLine from '@/components/elements/HorizontalLine/HorizontalLine';
import { useSnackbar } from '@/modules/core/hooks';
import { EditableBadge } from '@/modules/UseMyMoney/components';
import { CreatedProviderDto, CreateProviderDto, UpdatedProviderDto, UpdateProviderDto } from '@/modules/UseMyMoney/queries';
import { useDocumentOcrStore, useMyMoneyFormStore, useMyMoneyStore } from '@/modules/UseMyMoney/stores';
import { UseMyMoneyOption } from '@/modules/UseMyMoney/UseMyMoney.types';
import useProviderOptionRenderWrapper from '@/modules/UseMyMoney/widgets/FieldSetup/hooks/useProviderOptionRenderWrapper';
import useProviderOptions, { SORT_OPTIONS } from '@/modules/UseMyMoney/widgets/FieldSetup/hooks/useProviderOptions';

import FieldSetupLabel from '../FieldSetupLabel/FieldSetupLabel';
import FieldSetupOptions from '../FieldSetupOptions/FieldSetupOptions';

import AddProviderModal from './AddProviderModal/AddProviderModal';
import ProviderAddress from './ProviderAddress/ProviderAddress';
import ProviderModal from './ProviderModal/ProviderModal';
import ProviderDropdownPlaceholder from './ProviderDropdownPlaceholder';

interface Props {
  createProvider: (createProviderDto: CreateProviderDto) => Promise<CreatedProviderDto>;
  updateProvider: (createProviderDto: UpdateProviderDto) => Promise<UpdatedProviderDto>;
}

const ProviderFieldSetup: React.FC<Props> = ({ createProvider, updateProvider }) => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();
  const { handleAddPermanentSnackbar } = useSnackbar();
  const {
    values,
    automaticallyOpenProviderModal,
    handleSetFormValues,
    handleSetState,
  } = useMyMoneyFormStore();
  const { useMyMoneyOption } = useMyMoneyStore();
  const {
    suggestedProviderOptions,
    providerOptions,
    activeProviderOption,
    activeSuggestedProviderOption,
    provider,
  } = useProviderOptions({ customProviderId: values?.customProviderId });
  const { pushProvider } = useDocumentOcrStore();

  // reimburse myself modal
  const [isAddProviderModalVisible, setIsAddProviderModalVisible] = useState(false);
  // pay a provider modal
  const [isProviderModalVisible, setIsProviderModalVisible] = useState(false);
  const [editableProviderId, setEditableProviderId] = useState('');
  const [newProviderName, setNewProviderName] = useState('');

  const [searchProviderText, setSearchProviderText] = useState('');
  const [isEditMode, setIsEditMode] = useState(!!activeSuggestedProviderOption);

  const handleChangeProvider = useCallback((providerId: string | OptionKey) => {
    handleSetFormValues({ providerId: providerId as string, customProviderId: providerId as string });
    setIsEditMode(false);
  }, [handleSetFormValues]);

  const handleChangeSuggestedProvider = useCallback((providerId: string | OptionKey) => {
    handleSetFormValues({ providerId: providerId as string });
    setIsEditMode(false);
  }, [handleSetFormValues]);

  const handleOpenAddProviderModal = useCallback(() => {
    if (useMyMoneyOption === UseMyMoneyOption.PAY_A_PROVIDER) {
      setIsProviderModalVisible(true);
      return;
    }
    setIsAddProviderModalVisible(true);
  }, [useMyMoneyOption]);

  const handleEditProvider = useCallback((providerId?: string) => {
    if (!providerId) return;
    setEditableProviderId(providerId);
    setIsProviderModalVisible(true);
  }, []);

  const handleCreateProviderFromDropdown = useCallback(async (provideName: string) => {
    if (useMyMoneyOption === UseMyMoneyOption.PAY_A_PROVIDER) {
      setNewProviderName(provideName);
      setIsProviderModalVisible(true);
      return;
    }

    const createdProvider = await createProvider({
      facility_name: provideName,
    });
    if (!createdProvider?.id) {
      return;
    }
    handleChangeProvider(toString(createdProvider?.id));
    handleAddPermanentSnackbar({
      text: `${createdProvider?.facility_name} added.`,
      closeIcon: true,
      autoRemove: true,
    });
    pushProvider({
      id: toString(createdProvider?.id),
      name: createdProvider?.facility_name,
      isSuggested: false,
      address: {
        city: createdProvider?.addresses?.[0]?.city,
        line1: createdProvider?.addresses?.[0]?.line1,
        line2: createdProvider?.addresses?.[0]?.line2 || undefined,
        zipcode: createdProvider?.addresses?.[0]?.zipcode,
        state: {
          id: toString(createdProvider?.addresses?.[0]?.state?.id),
          code: createdProvider?.addresses?.[0]?.state?.code,
          name: createdProvider?.addresses?.[0]?.state?.name,
        },
      },
    });
  }, [useMyMoneyOption, createProvider, handleAddPermanentSnackbar, handleChangeProvider, pushProvider]);

  const { providerOptionRenderWrapper } = useProviderOptionRenderWrapper({ onEditProvider: handleEditProvider });

  useEffect(() => {
    if (values?.providerId && automaticallyOpenProviderModal) {
      handleEditProvider(values?.providerId);
      handleSetState({ automaticallyOpenProviderModal: false });
    }
  }, [handleEditProvider, handleSetState, values?.providerId, automaticallyOpenProviderModal]);

  return (
    <>
      <Box data-testid="provider-setup">
        <FieldSetupLabel
          testId="provider"
          hasValue={!!values?.providerId}
          title={t('Where did you incur this expense?')}
          isMobile={isMobile}
        />

        {suggestedProviderOptions?.length ? (
          <Box 
            data-testid="suggested-provider-options"
            margin={{ top: 'spacing24' }}
          >
            <Text weight={700}>
              {suggestedProviderOptions?.length > 1 
                ? t('Retailer/Merchants found on your document')
                : t('Retailer/Merchant found on your document')}
            </Text>
            <FieldSetupOptions
              testId="provider"
              margin={{ top: 'spacing8' }}
              value={values?.providerId}
              onChangeValue={handleChangeSuggestedProvider}
              options={suggestedProviderOptions}
            />
          </Box>
        ) : null}

        {/* Custom provider setup */}
        <Box margin={{ top: 'spacing24' }} align="start">
          <Text weight={700}>
            {values?.providerId
              ? t('Selected a Provider/ Merchant')
              : t('Not right? Select or add a new one')}
          </Text>
          <EditableBadge
            margin={{ top: 'spacing8' }}
            isEditMode={isEditMode}
            setIsEditMode={setIsEditMode}
            title={activeProviderOption?.title}
            value={values?.customProviderId as string}
            isSelected={values?.providerId === values?.customProviderId}
            onChangeValue={handleChangeProvider}
          >
            <Box
              margin={{ top: 'spacing8' }}
              direction="row"
              gap="spacing12"
              width="100%"
            >
              <DropdownInput
                testId="provider"
                name="provider"
                value={values?.customProviderId}
                onChangeValue={handleChangeProvider}
                options={providerOptions}
                placeholder={t('Search')}
                onCreate={handleCreateProviderFromDropdown}
                sortOptions={providerOptions?.length ? SORT_OPTIONS : undefined}
                defaultSortKey={SORT_OPTIONS?.[0]?.key}
                onSetSearchValue={setSearchProviderText}
                optionRenderWrapper={useMyMoneyOption === UseMyMoneyOption.PAY_A_PROVIDER
                  ? providerOptionRenderWrapper
                  : undefined}
                defaultAutoComplete="off"
                dropdownPlaceholder={(
                  <ProviderDropdownPlaceholder
                    hasOptions={!!providerOptions?.length}
                    searchProviderText={searchProviderText}
                  />
                )}
                clearSearchOnFocus
              />
              <AppButton
                testId="open-add-provider-modal"
                onClick={handleOpenAddProviderModal}
                buttonType="secondary"
                onlyIcon
              >
                <PlusThinIcon size="24px" />
              </AppButton>
            </Box>
          </EditableBadge>
        </Box>

        {useMyMoneyOption === UseMyMoneyOption.PAY_A_PROVIDER && provider ? (
          <Box>
            <HorizontalLine background="border2" />
            <ProviderAddress
              provider={provider}
              updateProvider={updateProvider}
              handleEditProvider={handleEditProvider}
            />
          </Box>
        ) : null}
      </Box>

      {isAddProviderModalVisible && (
        <AddProviderModal
          onSetVisible={setIsAddProviderModalVisible}
          createProvider={createProvider}
          handleChangeProvider={handleChangeProvider}
        />
      )}

      {isProviderModalVisible && (
        <ProviderModal
          onSetVisible={setIsProviderModalVisible}
          createProvider={createProvider}
          updateProvider={updateProvider}
          handleChangeProvider={handleChangeProvider}
          editableProviderId={editableProviderId}
          setEditableProviderId={setEditableProviderId}
          newProviderName={newProviderName}
          setNewProviderName={setNewProviderName}
        />
      )}
    </>
  );
};

export default ProviderFieldSetup;
